import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Card, CardBody, Spinner } from 'reactstrap';
import Router, { useRouter } from 'next/router';
import { useSurvey, useTest, useUnit } from 'hooks/units/index';
import useI18n from 'hooks/use-i18n';
import { fetchSurveyAsync, submitSurvey } from 'store/units/index';
import { getSelectedCentre } from 'store/master';
import { handleToastError, parseHtml } from 'helpers/index';
import { COURSE_STATUS, QUESTION_TYPE, UNIT_STATUS } from 'constants/constants';
import {
  showToastError,
  showToastInfo,
} from '@core/components/toastify/ToastTypes';

import ContentStart from '../ContentStart';
import ContentResult from './ContentResult/ContentResult';
import QuestionType from '../QuestionType/QuestionType';
import ButtonAnswer from './ButtonAnswer';
import unitCourseApi from '../../../../../api/unitCourseApi';
import { getColor, getColorText, getThemeColor } from 'store/settings';
import { FormProvider, useForm } from 'react-hook-form';
import { DataArray } from '@mui/icons-material';

const ContentSurvey = () => {
  const i18n = useI18n();
  const dispatch = useDispatch();
  const { query } = useRouter();
  const unitSelected = useUnit();
  const selectedCentre = useSelector(getSelectedCentre);
  const survey = useSurvey();
  const [isLoading, setIsLoading] = useState(false);
  const question = survey?.survey;
  const isOneQuestion = survey?.config?.isOneQuestion ? true : false;
  const [questionArray, setQuestionArray] = useState([]);
  const [timer, setTimer] = useState();
  const valueRef = useRef();

  const test = useTest();
  //DisableConditon
  let isDisabled = unitSelected?.status == COURSE_STATUS.INACTIVE;

  const handleStartSurvey = async () => {
    let interval = setInterval(trackUserCourseUnit, 5000);
    setTimer(interval);
    await dispatch(fetchSurveyAsync(unitSelected?._id));
  };

  async function trackUserCourseUnit() {
    const result = await unitCourseApi.trackUserCourseTest(unitSelected._id, 5);
  }
  //Color Theme
  const colorStatus = useSelector(getColor);
  const colorTextStatus = useSelector(getColorText);
  const themeStatus = useSelector(getThemeColor);
  const methods = useForm({
    defaultValues: {
      ratings: [],
      scalePointsList: {},
    },
    shouldUnregister: false,
  });

  const handleQuestion = () => {
    let array = [];
    if (question?.length) {
      question?.forEach((element) => {
        let obj = { ...element, answerValue: '' };
        array.push(obj);
      });
      setQuestionArray(array);
    }
  };

  useEffect(() => {
    valueRef.current = timer;
    !isOneQuestion && handleQuestion();
  }, [timer, question]);

  useEffect(() => {
    return () => {
      for (var i = 0; i < 1000; i++) {
        window.clearInterval(i);
      }
    };
  }, []);

  async function onSubmit(data) {
    const body = {
      data,
    };

    const params = {
      id: unitSelected?._id,
    };

    let param = {};

    if (isOneQuestion) {
      body.id = question?._id;
      params.survey = question?._id;
      if (survey?.current < survey.total) {
        params.next = survey?.current + 1;
      }
    }

    if (selectedCentre?._id) {
      param.centreId = selectedCentre?._id;
    }
    if (
      selectedCentre?.reviewContent ||
      selectedCentre?.reviewContent === undefined
    ) {
      param.reviewContent = true;
    }

    await dispatch(
      submitSurvey(params, body, unitSelected?._id, query?.slug, param),
    );
    setIsLoading(false);
  }

  async function submitFreetext() {
    const data = document.getElementById('freeText').value;

    if (!data && unitSelected?.preventContinue) {
      throw 'EMPTY_ANSWER';
    }

    await onSubmit(data);
  }

  async function submitMTC() {
    const data = question?.data?.map((answer, index) => {
      return document.getElementById(`MTP-${index}`)?.checked;
    });

    if (
      data?.findIndex((answer) => answer) === -1 &&
      unitSelected?.preventContinue
    ) {
      throw 'EMPTY_ANSWER';
    }

    await onSubmit(data);
  }

  const submitRating = async (answerValue, question = survey?.survey?.data) => {
    const { ratings } = methods.getValues();
    const ratingMap = new Map(answerValue ? answerValue : ratings);
    const result = question
      ?.map((quiz, index) =>
        ratingMap.has(index)
          ? ratingMap.get(index)
          : quiz?.SurveyOption === 'slider' && {
              ...quiz,
              answer: quiz?.value?.min,
            },
      )
      ?.filter(Boolean);

    if (isOneQuestion) {
      if (!ratingMap.size) {
        if (unitSelected?.preventContinue) {
          throw 'EMPTY_ANSWER';
        }
      }
      await onSubmit(result);
    } else {
      return result;
    }
  };

  const submitScaling = async () => {
    const { scalePointsList } = methods.getValues();
    if (unitSelected?.preventContinue) {
      if (!scalePointsList?.options?.length) {
        throw 'EMPTY_ANSWER';
      }
    }
    await onSubmit(scalePointsList);
    methods.setValue('scalePointsList', {});
  };

  async function handleSubmit() {
    if (survey?.current == survey.total) {
      clearInterval(valueRef.current);
      for (var i = 0; i < 1000; i++) {
        window.clearInterval(i);
      }
    }
    setIsLoading(true);
    try {
      switch (question?.type) {
        case QUESTION_TYPE.MTC:
          return await submitMTC();
        case QUESTION_TYPE.FREETEXT:
          return await submitFreetext();
        case QUESTION_TYPE.RATING:
          return await submitRating();
        case QUESTION_TYPE.SCALING:
          return await submitScaling();
        default:
          return null;
      }
    } catch (e) {
      setIsLoading(false);
      if (e === 'EMPTY_ANSWER') {
        showToastError('Empty answer, please try again');
      } else {
        handleToastError(e, i18n);
      }
    }
  }

  if (unitSelected?.status === UNIT_STATUS.COMPLETED) {
    return <ContentResult setIntervalId={timer} />;
  }

  const handleAllSubmit = async () => {
    let array = [];
    let obj = {};
    let data = undefined;
    for (const element of questionArray) {
      if (element?.answerValue || !survey?.config?.preventContinue) {
        if (element?.type === QUESTION_TYPE.MTC) {
          let checkData = [];
          if (element.answerValue) {
            checkData = element?.data.map(
              (element2) =>
                !!element.answerValue?.find((el) => el === element2),
            );
          }

          if (checkData.length) {
            obj = { data: checkData, id: element?._id };
            array.push(obj);
          }
        } else if (element?.type === QUESTION_TYPE.FREETEXT) {
          obj = { data: element.answerValue, id: element?._id };
          array.push(obj);
        } else if (element?.type === QUESTION_TYPE.RATING) {
          data = await submitRating(element.answerValue, element?.data);
          if (data) {
            obj = { data: data, id: element?._id };
            array.push(obj);
          }
        } else if (element?.type === QUESTION_TYPE.SCALING) {
          obj = { data: element.answerValue, id: element?._id };
          array.push(obj);
        }
      } else {
        showToastInfo('Please fill out every survey question');
        return;
      }
    }
    if (array.length) {
      await onSubmit(array);
    }
  };

  return (
    <Card style={{ zIndex: 1, marginTop: '3%' }}>
      <CardBody>
        {!survey && (
          <ContentStart
            content={`This survey contains ${
              unitSelected?.totalSurvey || 0
            } question(s).`}
            componentBtnStart={
              <button
                className="btn-bg-color"
                onClick={handleStartSurvey}
                disabled={survey?.isLoading || isDisabled}
                style={{
                  backgroundColor: `${colorStatus}`,
                  color: `${colorTextStatus}`,
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'white';
                  e.target.style.borderColor = colorStatus;
                  e.target.style.color = colorStatus;
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = colorStatus;
                  e.target.style.color = colorTextStatus;
                }}
              >
                Start survey
              </button>
            }
          />
        )}

        {survey?.isLoading && (
          <div className="d-flex justify-content-center">
            <Spinner style={{ color: `${themeStatus}` }} />
          </div>
        )}

        {isOneQuestion && question ? (
          <FormProvider {...methods}>
            <div className="d-flex">
              <span> {`${survey?.current})`}</span>
              {parseHtml(question?.content, 'ml-50 overflow-x-auto')}
            </div>

            <QuestionType
              type={question?.type}
              componentFooter={
                <ButtonAnswer
                  isOneQuestion={isOneQuestion}
                  isLoading={isLoading}
                  onSubmit={handleSubmit}
                  setIntervalId={timer}
                />
              }
              question={question}
              setIntervalId={timer}
            />
          </FormProvider>
        ) : survey && question?.length ? (
          <div>
            {question?.map((element, index) => (
              <div style={{ padding: 10 }} key={index}>
                <div className="d-flex flex-start">
                  {index + 1 + ')'}
                  {parseHtml(element?.content, 'ml-50 w-100 overflow-x-auto')}
                </div>
                <FormProvider {...methods}>
                  <QuestionType
                    type={element?.type}
                    componentFooter={
                      <ButtonAnswer
                        isOneQuestion={isOneQuestion}
                        isLoading={isLoading}
                        index={index}
                        onSubmit={handleSubmit}
                        setIntervalId={timer}
                      />
                    }
                    question={element}
                    setIntervalId={timer}
                    setQuestionArray={setQuestionArray}
                    questionArray={questionArray}
                  />
                </FormProvider>
                <hr />
              </div>
            ))}
            <button
              className="btn-bg-color"
              onClick={handleAllSubmit}
              style={{
                backgroundColor: `${colorStatus}`,
                color: `${colorTextStatus}`,
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'white';
                e.target.style.borderColor = colorStatus;
                e.target.style.color = colorStatus;
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = colorStatus;
                e.target.style.color = colorTextStatus;
              }}
            >
              Submit Survey
            </button>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
};

export default ContentSurvey;
